import { Injectable } from '@angular/core';
import { applyNonceToElement } from 'core/utils/csp-nonce';
import { BehaviorSubject, Subject } from 'rxjs';

declare global {
  interface Window {
    _genesysJs: string;
    Genesys: any;
  }
}

const GENESYS_SRC = 'https://apps.euw2.pure.cloud/genesys-bootstrap/genesys.min.js';
const GENESYS_ENVIRONMENT = 'euw2';
const GENESYS_WINDOW_STATE_KEY = '_{deploymentId}:gcmcopn';

export interface ChatData {
  deploymentId: string;
  userData: ChatUserData;
}

interface ChatUserData {
  siteId: string;
  memberId: string;
  name: string;
  phoneNumber: string;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class GenesysChatService {
  public opened$ = new BehaviorSubject<boolean>(false);
  public remove: () => void;
  public chatReady$ = new Subject<boolean>();
  public chatLoading$ = new Subject<boolean>();

  private get genesys(): any {
    return window.Genesys;
  }

  public open(data: ChatData) {
    if (this.genesys) {
      this.genesys('command', 'Messenger.open');
    } else {
      this.init(data.deploymentId, () => this.initSubscriptions(data.userData));
    }
  }

  public restoreOpen(deploymentId: string) {
    const windowStateKey = GENESYS_WINDOW_STATE_KEY.replace('{deploymentId}', deploymentId);
    const windowState = localStorage.getItem(windowStateKey);

    if (windowState) {
      return JSON.parse(windowState).value === 'true';
    } else {
      return false;
    }
  }

  private initSubscriptions(userData: ChatUserData) {
    this.genesys('subscribe', 'Messenger.ready', () => {
      this.genesys('command', 'Messenger.open');
      this.chatReady$.next(true);
    });

    this.genesys('subscribe', 'Database.ready', () => {
      this.genesys('command', 'Database.set', {
        messaging: {
          customAttributes: {
            Site_ID: userData.siteId,
            Member_ID: userData.memberId,
            Name: userData.name,
            Phone_Number: userData.phoneNumber,
            Email: userData.email,
          },
        },
      });
    });

    this.genesys('subscribe', 'Messenger.opened', () => {
      this.opened$.next(true);
    });

    this.genesys('subscribe', 'Messenger.closed', () => {
      this.opened$.next(false);
    });
  }

  public closeChat() {
    this.genesys('command', 'Messenger.close');
  }

  private init(deploymentId: string, onLoad: () => void) {
    this.chatLoading$.next(true);

    window._genesysJs = 'Genesys';
    window.Genesys = function (...args: any[]) {
      (window.Genesys.q = window.Genesys.q || []).push(args);
    };
    window.Genesys.t = new Date().getTime();
    window.Genesys.c = {
      deploymentId,
      environment: GENESYS_ENVIRONMENT,
    };
    const script = document.createElement('script');
    script.async = true;
    script.src = GENESYS_SRC;
    script.onload = () => onLoad();
    applyNonceToElement(script);

    document.head.appendChild(script);
  }
}
